// Upload flow shared by the dashboard dropzone and the Resumes page button.
// One file uploads at once: create the job, PUT the bytes, kick off
// extraction, land on the job page. Several files chosen together wait in
// `pending` — the user drags them into order and confirms — then become ONE
// job (English CV + Japanese 履歴書/職務経歴書): the first file names the
// job and attached originals follow the report in that order. Extraction
// runs server-side, so leaving the job page and coming back later is fine.

import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RESUME_ACCEPTED_TYPES, RESUME_MAX_BYTES } from "@agp/shared";
import { useApi } from "@/hooks/useApi";
import { uploadFile } from "@/lib/api";

export const RESUME_ACCEPT = `.pdf,.docx,.doc,.xlsx,.txt,${Object.keys(RESUME_ACCEPTED_TYPES).join(",")}`;

// Browsers leave file.type empty for TXT and report odd types for Office
// files now and then; the extension settles it.
const MIME_BY_EXT: Record<string, string> = Object.fromEntries(
  Object.entries(RESUME_ACCEPTED_TYPES).map(([mime, ext]) => [ext, mime]),
);
function detectMime(file: File): string | null {
  if (RESUME_ACCEPTED_TYPES[file.type]) return file.type;
  const ext = file.name.toLowerCase().split(".").pop() ?? "";
  return MIME_BY_EXT[ext] ?? null;
}

type Typed = { file: File; mimeType: string };

function typeFiles(files: File[]): { typed: Typed[] } | { error: string } {
  const typed: Typed[] = [];
  for (const file of files) {
    const mimeType = detectMime(file);
    if (!mimeType) {
      return {
        error: `${file.name}: unsupported file type — please upload PDF, DOCX, DOC, XLSX or TXT files.`,
      };
    }
    if (file.size > RESUME_MAX_BYTES) {
      return {
        error: `${file.name} is too large. The limit is ${Math.round(RESUME_MAX_BYTES / 1024 / 1024)} MB.`,
      };
    }
    typed.push({ file, mimeType });
  }
  return { typed };
}

export function useResumeUpload() {
  const api = useApi();
  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPendingState] = useState<File[] | null>(null);

  const upload = useCallback(
    async (typed: Typed[]) => {
      setUploading(true);
      try {
        const first = typed[0]!;
        const created = await api.post<{ jobId: string; uploadUrl: string }>("/api/resume-jobs", {
          filename: first.file.name,
          mimeType: first.mimeType,
          sizeBytes: first.file.size,
        });
        await uploadFile(created.uploadUrl, first.file, first.mimeType);
        for (const { file, mimeType } of typed.slice(1)) {
          const source = await api.post<{ sourceId: string; uploadUrl: string }>(
            `/api/resume-jobs/${created.jobId}/sources`,
            { filename: file.name, mimeType, sizeBytes: file.size },
          );
          await uploadFile(source.uploadUrl, file, mimeType);
        }
        await api.post(`/api/resume-jobs/${created.jobId}/extract`);
        setPendingState(null);
        navigate(`/jobs/${created.jobId}`);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Upload failed. Please try again.");
      } finally {
        setUploading(false);
      }
    },
    [api, navigate],
  );

  // Pasted candidate text — an email, notes — becomes a job without an
  // upload step; the backend stores it as a text source and extracts it.
  const pasteText = useCallback(
    async (text: string) => {
      setUploading(true);
      setError(null);
      try {
        const created = await api.post<{ jobId: string }>("/api/resume-jobs", { text });
        navigate(`/jobs/${created.jobId}`);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Could not create the resume. Please try again.");
      } finally {
        setUploading(false);
      }
    },
    [api, navigate],
  );

  const handleFiles = useCallback(
    (list: FileList | File[]) => {
      const files = Array.from(list);
      if (files.length === 0) return;
      setError(null);
      const checked = typeFiles(files);
      if ("error" in checked) {
        setError(checked.error);
        return;
      }
      if (pending) {
        setPendingState([...pending, ...files]);
        return;
      }
      if (files.length === 1) {
        void upload(checked.typed);
        return;
      }
      setPendingState(files);
    },
    [pending, upload],
  );

  const confirmPending = useCallback(() => {
    if (!pending || pending.length === 0) return;
    const checked = typeFiles(pending);
    if ("error" in checked) {
      setError(checked.error);
      return;
    }
    void upload(checked.typed);
  }, [pending, upload]);

  const setPending = useCallback((files: File[]) => setPendingState(files), []);
  const cancelPending = useCallback(() => setPendingState(null), []);

  return { handleFiles, pasteText, pending, setPending, confirmPending, cancelPending, uploading, error };
}
